import { dateFromTimeString, TimeString, toTimeString } from '@nextforce/api'
import { makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import React, { useCallback, useMemo } from 'react'
import { SectionList, SectionListData  } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { DateTimeChooser } from 'shared/components/list/date_time_chooser'

interface Props {
  startTime: TimeString
  endTime: TimeString
  description: string | undefined
  onStartTimeChange(startTime: TimeString): void
  onEndTimeChange(endTime: TimeString): void
}

interface BaseItem {
  key: string
}

interface StartTimeItem extends BaseItem {
  type: 'startTime'
}

interface EndTimeItem extends BaseItem {
  type: 'endTime'
}

type Item = StartTimeItem | EndTimeItem

type Section = SectionListData<
  Item,
  {
    name: string
    title?: string
  }
>

export function TimeRangeSettingView({
  startTime,
  endTime,
  description,
  onStartTimeChange,
  onEndTimeChange,
}: Props) {
  const styles = useStyles()
  const sections: Section[] = useMemo(() => {
    return [
      {
        name: 'dateTime',
        title: 'when',
        data: [
          {
            type: 'startTime',
            key: 'startTime',
          },
          {
            type: 'endTime',
            key: 'endTime',
          },
        ],
      },
    ]
  }, [])

  const renderItem = useCallback(
    ({ item }: { item: Item }) => {
      switch (item?.type) {
        case 'startTime':
          return (
            <DateTimeChooser
              title={t('startTime')}
              value={dateFromTimeString(startTime)}
              mode="time"
              minuteInterval={15}
              onChange={date => onStartTimeChange(toTimeString(date))}
            />
          )
        case 'endTime':
          return (
            <DateTimeChooser
              title={t('endTime')}
              value={dateFromTimeString(endTime)}
              mode="time"
              minuteInterval={15}
              onChange={date => onEndTimeChange(toTimeString(date))}
            />
          )
        default:
          return null
      }
    },
    [endTime, onEndTimeChange, onStartTimeChange, startTime]
  )

  const renderSectionFooter = useCallback(() => {
    return description ? (
      <Text style={styles.description}>{description}</Text>
    ) : null
  }, [description, styles.description])

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.container}>
      <SectionList
        sections={sections}
        renderItem={renderItem}
        renderSectionFooter={renderSectionFooter}
      />
    </SafeAreaView>
  )
}

export const useStyles = makeStyles(theme => ({
  container: {
    backgroundColor: theme.colors?.grey6,
    flex: 1,
  },
  description: {
    margin: 15,
  },
}))
